import { useEffect } from 'react';
import { fontById, FONTS, loadFont } from '../../lib/fonts';
import { DEFAULT_WIDGET_STYLE, panelVars, THEME_PANEL } from '../../lib/panelStyle';
import { useSettings } from '../../store/settings';
import { ghostButtonClass, inputClass, Section, Toggle } from './controls';

function Slider({ label, value, shown, min, max, step = 1, onChange }: {
  label: string;
  value: number;
  shown: string;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className="muted">{shown}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--accent)]"
      />
    </label>
  );
}

function ColourRow({ label, value, fallback, onChange, resetLabel }: {
  label: string;
  value: string;
  /** Shown in the picker when value is '' (the theme's own colour). */
  fallback: string;
  onChange: (v: string) => void;
  resetLabel?: string;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <input
        type="color"
        value={value || fallback}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-14 shrink-0 cursor-pointer rounded border border-[var(--border)] bg-transparent"
        aria-label={label}
      />
      <span className="flex-1">{label}</span>
      {resetLabel && value && (
        <button type="button" className={ghostButtonClass} onClick={() => onChange('')}>
          {resetLabel}
        </button>
      )}
    </div>
  );
}

const rgbToHex = (rgb: string) =>
  `#${rgb
    .split(' ')
    .map((n) => Number(n).toString(16).padStart(2, '0'))
    .join('')}`;

/** Settings → Appearance → Widget style: panel background, border, shadow and corners. */
export function WidgetStyleTab() {
  const style = useSettings((s) => s.widgetStyle);
  const theme = useSettings((s) => s.theme);
  const update = useSettings((s) => s.update);
  const set = (patch: Partial<typeof style>) => update('widgetStyle', patch);
  const themePanel = THEME_PANEL[theme];
  const opacity = style.opacity ?? themePanel.alpha;

  return (
    <>
      <div className="mb-8 rounded-xl bg-[url('/ads/kcl-clouds.jpg')] bg-cover bg-center p-6" style={panelVars(style, theme)}>
        <div className="glass p-4">
          <div className="font-heading text-lg">Preview</div>
          <div className="muted text-sm">How your widgets will look over a photo.</div>
        </div>
      </div>

      <Section title="Background">
        <Toggle label="Panel behind each widget" checked={style.background} onChange={(background) => set({ background })} />
        {style.background && (
          <>
            <ColourRow
              label="Panel colour"
              value={style.color}
              fallback={rgbToHex(themePanel.rgb)}
              onChange={(color) => set({ color })}
              resetLabel="Use theme colour"
            />
            <Slider
              label="Opacity"
              value={Math.round(opacity * 100)}
              shown={`${Math.round(opacity * 100)}%`}
              min={0}
              max={100}
              onChange={(v) => set({ opacity: v / 100 })}
            />
            <Toggle label="Frosted glass (blurs the photo behind)" checked={style.blur} onChange={(blur) => set({ blur })} />
          </>
        )}
        <Toggle label="Border" checked={style.border} onChange={(border) => set({ border })} />
      </Section>

      <Section title="Shadow">
        <Toggle label="Shadow behind each widget" checked={style.shadow} onChange={(shadow) => set({ shadow })} />
        {style.shadow && (
          <>
            <ColourRow label="Shadow colour" value={style.shadowColor} fallback="#000000" onChange={(shadowColor) => set({ shadowColor: shadowColor || '#000000' })} />
            <Slider
              label="Strength"
              value={Math.round(style.shadowStrength * 100)}
              shown={`${Math.round(style.shadowStrength * 100)}%`}
              min={5}
              max={100}
              onChange={(v) => set({ shadowStrength: v / 100 })}
            />
          </>
        )}
      </Section>

      <Section title="Corners">
        <Slider
          label="Roundness"
          value={style.radius}
          shown={style.radius === 0 ? 'Square' : `${style.radius} px`}
          min={0}
          max={40}
          onChange={(radius) => set({ radius })}
        />
      </Section>

      <button type="button" className={ghostButtonClass} onClick={() => update('widgetStyle', DEFAULT_WIDGET_STYLE)}>
        Back to the standard look
      </button>
    </>
  );
}

function FontPicker({ label, value, onChange, allowSame }: { label: string; value: string; onChange: (id: string) => void; allowSame?: boolean }) {
  const body = useSettings((s) => s.fonts.body);
  const shownId = value === 'same' ? body : value;
  useEffect(() => void loadFont(shownId), [shownId]);
  const kinds = [...new Set(FONTS.map((f) => f.kind))];
  return (
    <div className="space-y-2">
      <label className="block text-sm">
        <span className="mb-1 block">{label}</span>
        <select className={`${inputClass} bg-[var(--surface-strong)]`} value={value} onChange={(e) => onChange(e.target.value)}>
          {allowSame && <option value="same">Same as body text</option>}
          {kinds.map((k) => (
            <optgroup key={k} label={k}>
              {FONTS.filter((f) => f.kind === k).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="rounded-lg border border-[var(--border)] px-3 py-2 text-xl" style={{ fontFamily: fontById(shownId).family }}>
        The quick brown fox jumps over the lazy dog · 12:45
      </div>
    </div>
  );
}

/** Settings → Appearance → Fonts: one font for body text, one for headings, clock and headlines. */
export function FontsTab() {
  const fonts = useSettings((s) => s.fonts);
  const update = useSettings((s) => s.update);
  return (
    <>
      <Section title="Body text" description="Used for everything: widgets, news, lists and these menus.">
        <FontPicker label="Body font" value={fonts.body} onChange={(body) => update('fonts', { body })} />
      </Section>
      <Section title="Headings" description="The clock, temperatures, headlines, place names and titles.">
        <FontPicker label="Heading font" value={fonts.heading} onChange={(heading) => update('fonts', { heading })} allowSame />
      </Section>
    </>
  );
}
